import {
  CARRIER_STATUS_META,
  type CarrierStatus,
  OFF_PATH_CARRIER_STATUSES,
} from '@stopal/shared/carrier-status'
import {
  CircleAlert,
  CircleCheck,
  Clock,
  Handshake,
  PackageCheck,
  Timer,
  Truck,
} from 'lucide-react'
import type { StatusPresetMap } from './types'

// Single-source taxonomy: the value set and its wording live in
// @stopal/shared (the DB enum derives from the same array).
export type { CarrierStatus } from '@stopal/shared/carrier-status'

/**
 * What a third-party carrier is doing with a pallet. **NOT an Order Event
 * status** — a carrier shipment holds `in_transit` and `out_for_delivery`,
 * which no fleet trip ever holds, and it is a separate axis for exactly that
 * reason. Render a shipment through this map, never `orderEventStatusPresets`.
 *
 * Label and description come from `CARRIER_STATUS_META`, the shared registry
 * the transition service keys by, so the desk cannot show a state the service
 * refuses or name one differently from the board. This surface adds only the
 * web-specific bits: tone and icon.
 *
 * Keyed exhaustively over `CarrierStatus`, so a ninth status fails compilation
 * here rather than rendering as a raw enum string.
 *
 * **The two off-path states are the only `critical`/`warning` pair, and that is
 * load-bearing rather than decorative.** The desk and the board both have to
 * answer "is this one stuck?" at a glance, and a delayed shipment that reads in
 * the same tone as one progressing normally is the failure this whole column
 * exists to prevent. `offPathCarrierTones` below is the assertion that they
 * stay distinct.
 */
export const carrierStatusPresets: StatusPresetMap<CarrierStatus> = {
  booked: {
    label: CARRIER_STATUS_META.booked.label,
    description: CARRIER_STATUS_META.booked.description,
    tone: 'neutral',
    icon: Clock,
  },
  tendered: {
    label: CARRIER_STATUS_META.tendered.label,
    description: CARRIER_STATUS_META.tendered.description,
    tone: 'info',
    icon: Handshake,
  },
  picked_up: {
    label: CARRIER_STATUS_META.picked_up.label,
    description: CARRIER_STATUS_META.picked_up.description,
    tone: 'progress',
    icon: PackageCheck,
  },
  in_transit: {
    label: CARRIER_STATUS_META.in_transit.label,
    description: CARRIER_STATUS_META.in_transit.description,
    tone: 'progress',
    icon: Truck,
  },
  out_for_delivery: {
    label: CARRIER_STATUS_META.out_for_delivery.label,
    description: CARRIER_STATUS_META.out_for_delivery.description,
    tone: 'progress',
    icon: Truck,
  },
  delivered: {
    label: CARRIER_STATUS_META.delivered.label,
    description: CARRIER_STATUS_META.delivered.description,
    tone: 'success',
    icon: CircleCheck,
  },
  delayed: {
    label: CARRIER_STATUS_META.delayed.label,
    description: CARRIER_STATUS_META.delayed.description,
    tone: 'warning',
    icon: Timer,
  },
  exception: {
    label: CARRIER_STATUS_META.exception.label,
    description: CARRIER_STATUS_META.exception.description,
    tone: 'critical',
    icon: CircleAlert,
  },
}

/**
 * The tones the off-path states wear, derived from the shared predicate rather
 * than by naming `delayed` and `exception`.
 *
 * Exported so a test can assert that no on-path state shares one — which is the
 * property "a stuck shipment is distinguishable at a glance" actually reduces
 * to, and which nothing about the map above states on its own.
 */
export const offPathCarrierTones = OFF_PATH_CARRIER_STATUSES.map(
  (status) => carrierStatusPresets[status].tone,
)
